import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, ArrowLeft, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import BrandLogo from "../components/BrandLogo";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setResult(data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const copyLink = () => {
    if (!result?.reset_link) return;
    const fullLink = `${window.location.origin}${result.reset_link}`;
    navigator.clipboard.writeText(fullLink);
    setCopied(true);
    toast.success("Link copied");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-12" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 mb-8">
          <BrandLogo size="lg" />
          <span className="font-display text-2xl font-bold">TradeFlow UK</span>
        </Link>

        <h1 className="font-display text-3xl font-bold mb-2">Forgot password?</h1>
        <p className="text-zinc-600 mb-8">
          Enter the email on your account and we&apos;ll generate a reset link.
        </p>

        {!result ? (
          <form onSubmit={submit} className="space-y-5" data-testid="forgot-form">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1"
                data-testid="forgot-email"
              />
            </div>
            {err && <p className="text-sm text-red-600" data-testid="forgot-error">{err}</p>}
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-black hover:bg-zinc-800 text-white rounded-md py-6 font-semibold"
              data-testid="forgot-submit"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send reset link"}
            </Button>
          </form>
        ) : (
          <div className="space-y-5" data-testid="forgot-result">
            <div className="bg-[#FFF9D6] border border-[#FFCC00] rounded-lg p-5">
              <p className="font-display font-bold mb-1">Reset link generated</p>
              <p className="text-sm text-zinc-700">
                {result.message}
              </p>
            </div>

            {result.reset_link && (
              <div className="bg-black text-white rounded-lg p-5">
                <p className="font-mono text-[10px] uppercase tracking-widest text-[#FFCC00] mb-2">
                  Dev mode · No email service connected
                </p>
                <p className="text-sm text-zinc-300 mb-3">
                  Email isn&apos;t hooked up yet — copy your reset link below to continue.
                </p>
                <div className="flex items-center gap-2 bg-zinc-900 border border-zinc-700 rounded-md p-2">
                  <code className="text-xs text-zinc-300 truncate flex-1" data-testid="reset-link-text">
                    {window.location.origin}{result.reset_link}
                  </code>
                  <button
                    onClick={copyLink}
                    className="p-1.5 hover:bg-zinc-800 rounded"
                    data-testid="copy-reset-link"
                    aria-label="Copy link"
                  >
                    {copied ? <Check className="w-4 h-4 text-[#FFCC00]" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
                <Link
                  to={result.reset_link}
                  className="mt-4 block text-center bg-[#FFCC00] text-black font-semibold py-3 rounded-md"
                  data-testid="open-reset-link"
                >
                  Open reset page
                </Link>
              </div>
            )}
          </div>
        )}

        <div className="mt-6 text-sm">
          <Link to="/login" className="inline-flex items-center gap-1 text-zinc-600 hover:text-black" data-testid="back-to-login">
            <ArrowLeft className="w-4 h-4" /> Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
